import React, {
  useState, useCallback, useMemo, useRef,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import { getBoundActions } from '../../../../../actions/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import Close from '../../../../Icon/icons/Close/index'
import Check from '../../../../Icon/icons/Check/index'
import Sparkle from '../../../../Icon/icons/Sparkle/index'
import RefreshArrows from '../../../../Icon/icons/RefreshArrows/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import SparkleDivider from '../../../shared/SparkleDivider/index'
import CarouselDots from '../../../shared/CarouselDots/index'
import {
  GENERATE_IDENTITY_VIBE,
  SELECT_IDENTITY_VIBE,
} from '../../../../../services/community/query'
import { useCommunityModal } from '../../../../../services/community/shared/useCommunityModal'
import {
  ENERGY_OPTIONS,
  FEELS_TRUE_OPTIONS,
  SHAPING_DAYS_OPTIONS,
  MAX_ENERGY_PICKS,
  MAX_FEELS_PICKS,
  MAX_SHAPING_PICKS,
} from '../../../../../services/community/identity/profile/constants'
import type { OracleIdentityVibeGenerateInput } from '../../../../../graphql-types'
import {
  ARCHETYPE_PALETTE,
  colorForIndex,
  archetypeGradient,
  type ArchetypePalette,
} from './palette'

const STEPS = {
  ENERGY: 1, FEELS_TRUE: 2, SHAPING: 3, VIBE_LINES: 4,
} as const

const TOTAL_STEPS = 4

// Explicit lang-key maps because option strings include separators
// (apostrophes, ampersands, spaces) that don't transliterate cleanly into
// snake_case keys. Maps live here instead of constants.ts so the constants
// file stays a pure list of values shipped to the backend.
const ENERGY_KEYS: Record<string, string> = {
  Integrated: 'energy_integrated',
  Inspired: 'energy_inspired',
  Intuitive: 'energy_intuitive',
  Expressive: 'energy_expressive',
  Open: 'energy_open',
  Confident: 'energy_confident',
  Creative: 'energy_creative',
  Grounded: 'energy_grounded',
}

const FEELS_KEYS: Record<string, string> = {
  "I'm integrating something big": 'feels_integrating',
  "I'm in a season of clarity": 'feels_clarity',
  "I'm simplifying my life": 'feels_simplifying',
  "I'm reconnecting with myself": 'feels_reconnecting',
  "I'm asking deeper questions": 'feels_questions',
  "I'm navigating change": 'feels_change',
}

const SHAPING_KEYS: Record<string, string> = {
  'Rest & recovery': 'shaping_rest',
  Breathwork: 'shaping_breathwork',
  'Study & learning': 'shaping_study',
  Community: 'shaping_community',
  Service: 'shaping_service',
  'Letting go': 'shaping_letting_go',
  'Creative work': 'shaping_creative',
  'Movement & yoga': 'shaping_movement',
}

interface VibeQuizModalProps {
  onDismiss?: (() => void) | null
  onVibeGenerated?: ((vibeLine: string) => void) | null
  staticText: InternationalStrings
}

interface VibeQuizModalContentProps {
  onDismiss?: (() => void) | null
  onVibeGenerated?: ((vibeLine: string) => void) | null
}

// Chip button (steps 1 + 3) — rectangular bordered card. Step 1 renders a 56×56
// gradient circle above the label; step 3 colors the border per-option. On
// step 1 the selected state is signaled by a teal check badge in the
// bottom-right corner. Step 3 omits the check (`showCheck={false}`) and
// signals selection via the `--selected` CSS rule (inset shadow that visually
// doubles the palette border without shifting layout). Own component so it
// can call `useTranslatedText` inside a `.map()` without violating the rules
// of hooks.
interface VibeChipProps {
  option: string
  langKey: string
  isSelected: boolean
  isDisabled: boolean
  size?: 'sm' | 'lg'
  /** Step 1 + step 3 supply this. */
  palette?: ArchetypePalette
  /** Step 1 only — render the 56×56 gradient circle above the label. */
  showCircle?: boolean
  /** Step 3 only — color the default border with `palette.primary`. */
  borderFromPalette?: boolean
  /** Step 3 passes `false` — selection is signaled by the `--selected` CSS rule. */
  showCheck?: boolean
  onClick: () => void
}

function VibeChip ({
  option,
  langKey,
  isSelected,
  isDisabled,
  size = 'sm',
  palette,
  showCircle = false,
  borderFromPalette = false,
  showCheck = true,
  onClick,
}: VibeChipProps) {
  const label = useTranslatedText({ keys: [langKey] })
  const className = [
    'vibe-quiz-modal__chip',
    size === 'lg' ? 'vibe-quiz-modal__chip--lg' : '',
    showCircle ? 'vibe-quiz-modal__chip--with-circle' : '',
    isSelected ? 'vibe-quiz-modal__chip--selected' : '',
    isDisabled ? 'vibe-quiz-modal__chip--disabled' : '',
  ].filter(Boolean).join(' ')
  // Chips with a palette border keep that color in every state — selection is
  // signaled by the teal check badge, not a border swap (per node 3687:55903).
  // Dynamic color is passed via a CSS custom property on the wrapper because
  // Gaia's Button mis-declares `style: PropTypes.string` (would warn on an object).
  const wrapperStyle = borderFromPalette && palette
    ? ({ '--vibe-chip-border-color': palette.primary } as React.CSSProperties)
    : undefined
  return (
    <div className="vibe-quiz-modal__chip-wrap" style={wrapperStyle}>
      <Button
        type={BUTTON_TYPES.GHOST}
        className={className}
        onClick={onClick}
        disabled={isDisabled}
      >
        {showCircle && palette ? (
          <span
            className="vibe-quiz-modal__archetype-circle"
            style={{ backgroundImage: archetypeGradient(palette) }}
            aria-hidden="true"
          />
        ) : null}
        <span className="vibe-quiz-modal__chip-label-row">
          {/* Step 1 only: phantom spacer on the left so the label is the
              geometric center of the row → label sits centered under the
              circle. Step 3 (no circle, no check) skips the phantom entirely
              since there's no opposite-side check to balance against. */}
          {showCircle && showCheck ? (
            <span
              className="vibe-quiz-modal__chip-check vibe-quiz-modal__chip-check--phantom"
              aria-hidden="true"
            />
          ) : null}
          <span className="vibe-quiz-modal__chip-label">
            {label && label !== langKey ? label : option}
          </span>
          {/* Right-side check — always rendered so the row's width is constant
              (no layout shift on click); `--hidden` hides it when not selected.
              Step 3 opts out via `showCheck={false}` and relies on the CSS
              `--selected` rule for its selection cue instead. */}
          {showCheck ? (
            <span
              className={[
                'vibe-quiz-modal__chip-check',
                isSelected ? '' : 'vibe-quiz-modal__chip-check--hidden',
              ].filter(Boolean).join(' ')}
              aria-hidden="true"
            >
              <Check />
            </span>
          ) : null}
        </span>
      </Button>
    </div>
  )
}

// Checkbox row (steps 2 + 4). Native input is visually hidden but remains
// focusable + clickable via the wrapping `<label>`. When `palette` is
// supplied, the row's default border uses `palette.primary`; the selected
// state still swaps to teal (per Figma node 3687:55903).
//
// Two flavors below — `VibeCheckboxRowTranslated` (label resolves through
// staticText via `useTranslatedText`) and `VibeCheckboxRowRaw` (label is
// already a final string, e.g. the AI-generated vibe line at step 4).
// Split rather than threading an optional `langKey` so we never have to call
// `useTranslatedText` with a throwaway sentinel key.
interface VibeCheckboxRowBaseProps {
  isSelected: boolean
  isDisabled: boolean
  palette?: ArchetypePalette
  onChange: () => void
}

// Pass the palette color as a CSS custom property so the SCSS can reference
// it from both the base border rule and the `--selected` inset-shadow rule
// (the latter doubles the border per Figma node 3687:55903). Mirrors the
// chip pattern's `--vibe-chip-border-color`.
function checkboxRowStyle (palette?: ArchetypePalette): React.CSSProperties | undefined {
  return palette
    ? ({ '--vibe-checkbox-row-border-color': palette.primary } as React.CSSProperties)
    : undefined
}

function checkboxRowClassName (isSelected: boolean, isDisabled: boolean): string {
  return [
    'vibe-quiz-modal__checkbox-row',
    isSelected ? 'vibe-quiz-modal__checkbox-row--selected' : '',
    isDisabled ? 'vibe-quiz-modal__checkbox-row--disabled' : '',
  ].filter(Boolean).join(' ')
}

interface VibeCheckboxRowTranslatedProps extends VibeCheckboxRowBaseProps {
  option: string
  langKey: string
}

function VibeCheckboxRowTranslated ({
  option, langKey, isSelected, isDisabled, palette, onChange,
}: VibeCheckboxRowTranslatedProps) {
  const translated = useTranslatedText({ keys: [langKey] })
  // Fall back to `option` if the translation lookup returns the bare key
  // (the static-text helper signals "no value" by echoing the key back).
  const label = translated && translated !== langKey ? translated : option
  return (
    <label
      className={checkboxRowClassName(isSelected, isDisabled)}
      style={checkboxRowStyle(palette)}
    >
      <input
        type="checkbox"
        checked={isSelected}
        disabled={isDisabled}
        onChange={onChange}
      />
      <span className="vibe-quiz-modal__checkbox-box" aria-hidden="true">
        <Check />
      </span>
      <span className="vibe-quiz-modal__checkbox-label">{label}</span>
    </label>
  )
}

interface VibeCheckboxRowRawProps extends VibeCheckboxRowBaseProps {
  label: string
}

function VibeCheckboxRowRaw ({
  label, isSelected, isDisabled, palette, onChange,
}: VibeCheckboxRowRawProps) {
  return (
    <label
      className={checkboxRowClassName(isSelected, isDisabled)}
      style={checkboxRowStyle(palette)}
    >
      <input
        type="checkbox"
        checked={isSelected}
        disabled={isDisabled}
        onChange={onChange}
      />
      <span className="vibe-quiz-modal__checkbox-box" aria-hidden="true">
        <Check />
      </span>
      <span className="vibe-quiz-modal__checkbox-label">{label}</span>
    </label>
  )
}

function VibeQuizModalContent ({
  onDismiss = null,
  onVibeGenerated = null,
}: VibeQuizModalContentProps) {
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') as string | undefined
  const language = usePrimaryAppLanguage()
  const dispatch = useDispatch()
  // `getBoundActions` returns a fresh object reference on every call.
  // Memoize so `dismissModal` stays referentially stable across renders —
  // important because `handleClose` is fed into `useCommunityModal` below,
  // and an unstable `onClose` would re-attach its Escape/focus-trap listeners
  // on every render.
  const actions = useMemo(() => getBoundActions(dispatch), [dispatch])
  const { dismissModal } = actions.dialog

  const [step, setStep] = useState<number>(STEPS.ENERGY)

  const [selectedEnergy, setSelectedEnergy] = useState<string[]>([])
  const [selectedFeels, setSelectedFeels] = useState<string[]>([])
  const [selectedShaping, setSelectedShaping] = useState<string[]>([])

  const [vibeLines, setVibeLines] = useState<string[]>([])
  const [selectedVibe, setSelectedVibe] = useState<string | null>(null)
  const [vibeError, setVibeError] = useState<string | null>(null)

  const [generateVibe, { loading: generating }] = useMutation(
    GENERATE_IDENTITY_VIBE,
    { context: { jwt }, errorPolicy: 'all' },
  )
  const [selectVibe, { loading: selecting }] = useMutation(
    SELECT_IDENTITY_VIBE,
    { context: { jwt }, errorPolicy: 'all' },
  )

  const dialogRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    if (onDismiss) onDismiss()
    else dismissModal()
  }, [onDismiss, dismissModal])

  // A11Y-3 + UX-1: unified scroll lock, Escape key, and focus trap.
  useCommunityModal({ dialogRef, onClose: handleClose })

  const labelText = useTranslatedText({ keys: ['label'] })
  const closeText = useTranslatedText({ keys: ['close'] })
  const backText = useTranslatedText({ keys: ['back'] })
  const cancelText = useTranslatedText({ keys: ['cancel'] })
  const nextText = useTranslatedText({ keys: ['next'] })
  const createText = useTranslatedText({ keys: ['create'] })
  const saveText = useTranslatedText({ keys: ['save'] })
  const tryAgainText = useTranslatedText({ keys: ['tryAgain'] })
  const energyQuestionText = useTranslatedText({ keys: ['energyQuestion'] })
  const energyHintText = useTranslatedText({ keys: ['energyHint'] })
  const feelsQuestionText = useTranslatedText({ keys: ['feelsQuestion'] })
  const feelsHintText = useTranslatedText({ keys: ['feelsHint'] })
  const shapingQuestionText = useTranslatedText({ keys: ['shapingQuestion'] })
  const shapingHintText = useTranslatedText({ keys: ['shapingHint'] })
  const vibeLineQuestionText = useTranslatedText({ keys: ['vibeLineQuestion'] })
  const vibeLineHintText = useTranslatedText({ keys: ['vibeLineHint'] })
  const generatingVibesText = useTranslatedText({ keys: ['generatingVibes'] })
  const noVibeLinesText = useTranslatedText({ keys: ['noVibeLines'] })
  const generateFailedText = useTranslatedText({ keys: ['generateFailed'] })
  const saveFailedText = useTranslatedText({ keys: ['saveFailed'] })

  const handleGenerate = useCallback(async () => {
    const previousLines = vibeLines.length > 0 ? [...vibeLines] : undefined
    setVibeError(null)
    setVibeLines([])
    setSelectedVibe(null)
    try {
      const input: OracleIdentityVibeGenerateInput = {
        vibeQuiz: {
          energy: selectedEnergy,
          feelsTrue: selectedFeels,
          shapingDays: selectedShaping,
        },
        language,
      }
      if (previousLines && previousLines.length > 0) {
        input.previousVibeLines = previousLines
      }
      const result = await generateVibe({ variables: { input } })
      const response = result.data?.oracleIdentityVibeGenerate
      if (response?.success && response.vibeLines?.length > 0) {
        setVibeLines(response.vibeLines)
      } else if (response?.errors?.length) {
        setVibeError(response.errors[0])
      } else {
        setVibeError(noVibeLinesText)
      }
    } catch {
      setVibeError(generateFailedText)
    }
  }, [
    generateVibe,
    noVibeLinesText,
    generateFailedText,
    selectedEnergy,
    selectedFeels,
    selectedShaping,
    vibeLines,
    language,
  ])

  const handleNext = useCallback(() => {
    if (step === STEPS.ENERGY && selectedEnergy.length > 0) {
      setStep(STEPS.FEELS_TRUE)
    } else if (step === STEPS.FEELS_TRUE && selectedFeels.length > 0) {
      setStep(STEPS.SHAPING)
    } else if (step === STEPS.SHAPING && selectedShaping.length > 0) {
      setStep(STEPS.VIBE_LINES)
      handleGenerate()
    }
  }, [step, selectedEnergy, selectedFeels, selectedShaping, handleGenerate])

  // Back/cancel — closes the modal on the first step (no previous quiz state
  // to return to) and steps backwards through the form on later steps. Hidden
  // on the final step where the dedicated "Try again" CTA handles regenerate.
  const handleBack = useCallback(() => {
    if (step === STEPS.ENERGY) {
      handleClose()
    } else if (step === STEPS.FEELS_TRUE) {
      setStep(STEPS.ENERGY)
    } else if (step === STEPS.SHAPING) {
      setStep(STEPS.FEELS_TRUE)
    }
  }, [step, handleClose])

  const toggleSelection = useCallback((
    item: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    max: number,
  ) => {
    if (selected.includes(item)) {
      setSelected(selected.filter((s) => s !== item))
    } else if (selected.length < max) {
      setSelected([...selected, item])
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedVibe) return
    try {
      const result = await selectVibe({
        variables: { input: { vibeLine: selectedVibe } },
      })
      const response = result.data?.oracleIdentityVibeSelect
      if (response?.success) {
        if (onVibeGenerated) onVibeGenerated(selectedVibe)
        handleClose()
      } else {
        setVibeError(response?.errors?.[0] || saveFailedText)
      }
    } catch {
      setVibeError(saveFailedText)
    }
  }, [selectedVibe, selectVibe, onVibeGenerated, handleClose, saveFailedText])

  const canGoNext = useMemo(() => {
    if (step === STEPS.ENERGY) return selectedEnergy.length > 0
    if (step === STEPS.FEELS_TRUE) return selectedFeels.length > 0
    if (step === STEPS.SHAPING) return selectedShaping.length > 0
    return false
  }, [step, selectedEnergy.length, selectedFeels.length, selectedShaping.length])

  // Pick the question + hint for the current step. Lives in the header so the
  // step body becomes a pure options container.
  const { currentQuestion, currentHint } = useMemo(() => {
    if (step === STEPS.ENERGY) {
      return { currentQuestion: energyQuestionText, currentHint: energyHintText }
    }
    if (step === STEPS.FEELS_TRUE) {
      return { currentQuestion: feelsQuestionText, currentHint: feelsHintText }
    }
    if (step === STEPS.SHAPING) {
      return { currentQuestion: shapingQuestionText, currentHint: shapingHintText }
    }
    return { currentQuestion: vibeLineQuestionText, currentHint: vibeLineHintText }
  }, [
    step,
    energyQuestionText, energyHintText,
    feelsQuestionText, feelsHintText,
    shapingQuestionText, shapingHintText,
    vibeLineQuestionText, vibeLineHintText,
  ])

  const renderChipGrid = (
    options: string[],
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    max: number,
    keyMap: Record<string, string>,
    size: 'sm' | 'lg' = 'sm',
    paletteMode: 'circle' | 'border' | 'none' = 'none',
    showCheck = true,
  ) => (
    <div className="vibe-quiz-modal__option-grid">
      {options.map((option, i) => {
        const isSelected = selected.includes(option)
        const isDisabled = !isSelected && selected.length >= max
        // Step 1: archetype-keyed gradient circles (palette per option name).
        // Step 3: positional cycle through PALETTE_ORDER (8 shaping options ↔ 8 palette colors).
        let palette
        if (paletteMode === 'circle') palette = ARCHETYPE_PALETTE[option]
        else if (paletteMode === 'border') palette = colorForIndex(i)
        return (
          <VibeChip
            key={option}
            option={option}
            langKey={keyMap[option] || option}
            isSelected={isSelected}
            isDisabled={isDisabled}
            size={size}
            palette={palette}
            showCircle={paletteMode === 'circle'}
            borderFromPalette={paletteMode === 'border'}
            showCheck={showCheck}
            onClick={() => toggleSelection(option, selected, setSelected, max)}
          />
        )
      })}
    </div>
  )

  // Two-column checkbox layout for step 2 — splits FEELS_TRUE_OPTIONS evenly.
  // `globalIndex` cycles palette colors across the visual reading order: left
  // column first (orange / purple / green), then right column (red / yellow / blue).
  const renderCheckboxColumns = (
    options: string[],
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    max: number,
    keyMap: Record<string, string>,
  ) => {
    const half = Math.ceil(options.length / 2)
    const left = options.slice(0, half)
    const right = options.slice(half)
    const renderRow = (option: string, globalIndex: number) => {
      const isSelected = selected.includes(option)
      const isDisabled = !isSelected && selected.length >= max
      return (
        <VibeCheckboxRowTranslated
          key={option}
          option={option}
          langKey={keyMap[option] || option}
          isSelected={isSelected}
          isDisabled={isDisabled}
          palette={colorForIndex(globalIndex)}
          onChange={() => toggleSelection(option, selected, setSelected, max)}
        />
      )
    }
    return (
      <div className="vibe-quiz-modal__checkbox-columns">
        <div className="vibe-quiz-modal__checkbox-column">
          {left.map((option, i) => renderRow(option, i))}
        </div>
        <div className="vibe-quiz-modal__checkbox-column">
          {right.map((option, i) => renderRow(option, half + i))}
        </div>
      </div>
    )
  }

  // Single-column checkbox list for step 4 (vibe-line selection). The model
  // is single-select even though the affordance reads as a checkbox per Figma.
  // Palette cycles in the order vibe lines come back from the AI mutation.
  // Uses the `Raw` flavor — vibe lines come from the AI mutation as final
  // strings, not staticText keys.
  const renderVibeLineList = () => (
    <div className="vibe-quiz-modal__checkbox-column vibe-quiz-modal__checkbox-column--full">
      {vibeLines.map((line, i) => (
        <VibeCheckboxRowRaw
          key={line}
          label={line}
          isSelected={selectedVibe === line}
          isDisabled={false}
          palette={colorForIndex(i)}
          onChange={() => setSelectedVibe(line)}
        />
      ))}
    </div>
  )

  return (
    <div
      className="vibe-quiz-modal__overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="vibe-quiz-modal"
        onClick={(e) => e.stopPropagation()}
        // No-op `onKeyDown` placates jsx-a11y/click-events-have-key-events
        // (we have `onClick` on this stopPropagation surface). Real keyboard
        // handling — Escape to close, Tab to cycle — lives in
        // `useCommunityModal({ dialogRef, onClose: handleClose })` above.
        onKeyDown={() => {}}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vibe-quiz-modal-question"
        tabIndex={-1}
      >
        <div className="vibe-quiz-modal__header">
          <div className="vibe-quiz-modal__header-content">
            <div className="vibe-quiz-modal__label-row">
              <span className="vibe-quiz-modal__label-icon" aria-hidden="true">
                <Sparkle />
              </span>
              <span className="vibe-quiz-modal__label">{labelText}</span>
            </div>
            <h2 id="vibe-quiz-modal-question" className="vibe-quiz-modal__question">{currentQuestion}</h2>
            <p className="vibe-quiz-modal__hint">{currentHint}</p>
          </div>
          <Button
            type={BUTTON_TYPES.GHOST}
            className="vibe-quiz-modal__close-btn"
            onClick={handleClose}
            aria-label={closeText}
          >
            <Close />
          </Button>
        </div>

        <SparkleDivider className="vibe-quiz-modal__divider" />

        {step === STEPS.ENERGY ? (
          <div className="vibe-quiz-modal__step">
            {renderChipGrid(
              ENERGY_OPTIONS,
              selectedEnergy,
              setSelectedEnergy,
              MAX_ENERGY_PICKS,
              ENERGY_KEYS,
              'sm',
              'circle',
            )}
          </div>
        ) : null}

        {step === STEPS.FEELS_TRUE ? (
          <div className="vibe-quiz-modal__step">
            {renderCheckboxColumns(
              FEELS_TRUE_OPTIONS,
              selectedFeels,
              setSelectedFeels,
              MAX_FEELS_PICKS,
              FEELS_KEYS,
            )}
          </div>
        ) : null}

        {step === STEPS.SHAPING ? (
          <div className="vibe-quiz-modal__step">
            {renderChipGrid(
              SHAPING_DAYS_OPTIONS,
              selectedShaping,
              setSelectedShaping,
              MAX_SHAPING_PICKS,
              SHAPING_KEYS,
              'lg',
              'border',
              false, // no check — shaping uses the `--selected` CSS cue
            )}
          </div>
        ) : null}

        {step === STEPS.VIBE_LINES ? (
          <div className="vibe-quiz-modal__step">
            {generating ? (
              <div className="vibe-quiz-modal__loading">
                <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
                <p className="vibe-quiz-modal__loading-text">{generatingVibesText}</p>
              </div>
            ) : null}

            {vibeError && !generating ? (
              <div className="vibe-quiz-modal__error">
                <p className="vibe-quiz-modal__error-text">{vibeError}</p>
                <Button
                  type={BUTTON_TYPES.GHOST}
                  className="vibe-quiz-modal__error-retry"
                  onClick={handleGenerate}
                >
                  {tryAgainText}
                </Button>
              </div>
            ) : null}

            {vibeLines.length > 0 && !generating ? renderVibeLineList() : null}
          </div>
        ) : null}

        <div className="vibe-quiz-modal__footer">
          {step !== STEPS.VIBE_LINES ? (
            <Button
              type={BUTTON_TYPES.GHOST}
              className="vibe-quiz-modal__back-btn"
              onClick={handleBack}
            >
              {step === STEPS.ENERGY ? cancelText : backText}
            </Button>
          ) : null}

          {step === STEPS.ENERGY || step === STEPS.FEELS_TRUE ? (
            <Button
              className="vibe-quiz-modal__primary-btn"
              onClick={handleNext}
              disabled={!canGoNext}
            >
              {nextText}
            </Button>
          ) : null}

          {step === STEPS.SHAPING ? (
            <Button
              className="vibe-quiz-modal__primary-btn"
              onClick={handleNext}
              disabled={!canGoNext}
            >
              {createText}
            </Button>
          ) : null}

          {/* Only show the dual-button step-4 footer once vibe lines have
              loaded. While generating, the loading spinner takes the focus;
              while erroring, the inline error block carries its own retry. */}
          {step === STEPS.VIBE_LINES && !generating && vibeLines.length > 0 ? (
            <>
              <Button
                type={BUTTON_TYPES.GHOST}
                className="vibe-quiz-modal__try-again-btn"
                onClick={handleGenerate}
              >
                <RefreshArrows />
                {tryAgainText}
              </Button>
              <Button
                className="vibe-quiz-modal__primary-btn"
                onClick={handleSave}
                disabled={!selectedVibe || selecting}
              >
                {saveText}
              </Button>
            </>
          ) : null}
        </div>

        <CarouselDots
          total={TOTAL_STEPS}
          current={step}
          variant="phased"
          className="vibe-quiz-modal__dots"
        />
      </div>
    </div>
  )
}

function VibeQuizModal ({
  onDismiss = null,
  onVibeGenerated = null,
  staticText,
}: VibeQuizModalProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <VibeQuizModalContent
        onDismiss={onDismiss}
        onVibeGenerated={onVibeGenerated}
      />
    </StaticTextProvider>
  )
}

const ConnectedVibeQuizModal = connectStaticText({
  storeKey: 'vibeQuizModal',
})(VibeQuizModal)

export { VibeQuizModal }
export type { VibeQuizModalProps }

export default ConnectedVibeQuizModal
